/**
 * Verified-2026 audio codec support matrix (PRD §8 "Audio codec support" /
 * AC 5.7 codec support contract).
 *
 * Contract facts encoded here (and ONLY these — nothing beyond the verified
 * list is asserted):
 * - MP3 and M4A/AAC decode natively on BOTH target engines (Chrome/Android,
 *   Safari/iOS).
 * - FLAC decodes in Chrome and in Safari (iOS 11+ / macOS 13+, progressive
 *   download) — every 2026 target device exceeds that floor, so the Safari
 *   entry is `native` rather than version-gated.
 * - OGG Vorbis decodes in all Chromium/Firefox, and in Safari ONLY at
 *   iOS/macOS 18.4+ (Safari ≤ 18.3 / iOS ≤ 17.3 do NOT decode it).
 *
 * Firefox appears in the verified contract only for OGG; its other formats are
 * marked `unverified` (treated as unsupported by the boolean helper) rather
 * than guessed. Per AC 5.7 the CLIENT feature-detects at runtime
 * (`HTMLMediaElement.canPlayType` probe — TASK 8.2 `codec-detect.ts`); this
 * matrix is the documented baseline the probe results are validated against,
 * never a substitute for the probe.
 */
export const AUDIO_FORMATS = ['mp3', 'aac', 'flac', 'ogg'];
export const BROWSER_ENGINES = ['chromium', 'firefox', 'safari'];
/** MIME strings for `canPlayType` probes per catalog format. */
export const FORMAT_MIME_TYPES = {
    mp3: 'audio/mpeg',
    aac: 'audio/mp4; codecs="mp4a.40.2"',
    flac: 'audio/flac',
    ogg: 'audio/ogg; codecs="vorbis"',
};
/** Safari (iOS/macOS) first decodes OGG Vorbis at 18.4 (verified 2026). */
export const SAFARI_OGG_MIN_VERSION = { major: 18, minor: 4 };
export const CODEC_MATRIX = {
    mp3: {
        chromium: { kind: 'native' },
        firefox: { kind: 'unverified' },
        safari: { kind: 'native' },
    },
    aac: {
        chromium: { kind: 'native' },
        firefox: { kind: 'unverified' },
        safari: { kind: 'native' },
    },
    flac: {
        chromium: { kind: 'native' },
        firefox: { kind: 'unverified' },
        safari: { kind: 'native' },
    },
    ogg: {
        chromium: { kind: 'native' },
        firefox: { kind: 'native' },
        safari: { kind: 'min-version', minVersion: SAFARI_OGG_MIN_VERSION },
    },
};
/** Parse `"18.4"` / `"18"` into an {@link EngineVersion}; `undefined` if unparseable. */
export function parseEngineVersion(version) {
    const match = /^(\d+)(?:\.(\d+))?/.exec(version.trim());
    if (match === null || match[1] === undefined) {
        return undefined;
    }
    return { major: Number(match[1]), minor: match[2] === undefined ? 0 : Number(match[2]) };
}
function atLeast(version, min) {
    return version.major > min.major || (version.major === min.major && version.minor >= min.minor);
}
/**
 * Baseline lookup. For version-gated rules an unknown/unparseable engine
 * version yields `'unverified'` (conservative — never claim support that the
 * contract cannot verify; the runtime `canPlayType` probe has the final word).
 */
export function codecSupport(format, engine, engineVersion) {
    const rule = CODEC_MATRIX[format][engine];
    switch (rule.kind) {
        case 'native':
            return 'supported';
        case 'none':
            return 'unsupported';
        case 'unverified':
            return 'unverified';
        case 'min-version': {
            if (engineVersion === undefined) {
                return 'unverified';
            }
            const parsed = parseEngineVersion(engineVersion);
            if (parsed === undefined) {
                return 'unverified';
            }
            return atLeast(parsed, rule.minVersion) ? 'supported' : 'unsupported';
        }
    }
}
/** `true` only when the verified matrix positively supports the combination. */
export function isFormatSupported(format, engine, engineVersion) {
    return (codecSupport(format, engine, ...(engineVersion === undefined ? [] : [engineVersion])) ===
        'supported');
}
//# sourceMappingURL=codec-matrix.js.map